// Vendored from @offlinesync/react-native (offline-platforms/mobile-sdk-react-native).
//
// The SDK is not published to npm, so its delta-sync client + hooks are vendored here to keep the Expo app
// self-contained and EAS-buildable. The protocol logic is faithful to the upstream OfflineSync client; the
// only adaptation is a DOM-free `FetchLike` type so it typechecks under Expo's strict config without lib.dom.
import { useEffect, useState } from 'react';

export type StreamName = 'article' | 'client' | 'document' | 'stock' | 'paiement' | string;
export type Operation = 'UPSERT' | 'DELETE';

/** Minimal fetch shape so we don't depend on lib.dom typings. React Native provides a global fetch. */
export type FetchLike = (
  input: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<{ ok: boolean; status: number; text(): Promise<string>; json(): Promise<unknown> }>;

export interface NetworkMonitor {
  isOnline(): Promise<boolean>;
  subscribe(listener: (online: boolean) => void): () => void;
}

export interface SyncConfig {
  baseUrl: string;
  apiKey: string;
  deviceId: string;
  pdvCode?: string;
  appVersion?: string;
  platform?: string;
  streams: StreamName[];
  database: LocalDatabase;
  fetch?: FetchLike;
  network?: NetworkMonitor;
  pageSize?: number;
  bootstrapWithSnapshot?: boolean;
}

export interface Change {
  sequenceId: number;
  stream: string;
  entityId: string;
  operation: Operation;
  version: number;
  payload: Record<string, unknown> | null;
  occurredAt: string;
}

export interface OutboxEvent {
  eventId: string;
  stream: string;
  entityId: string;
  operation: Operation;
  baseVersion?: number;
  payload?: Record<string, unknown> | null;
  status?: 'PENDING' | 'SENT' | 'FAILED';
}

export interface SyncStatus {
  state: 'UNINITIALIZED' | 'IDLE' | 'SYNCING' | 'OFFLINE' | 'ERROR';
  lastSyncAt?: string;
  error?: string;
  pendingOutbox: number;
}

export interface Diagnostics {
  status: SyncStatus;
  checkpoints: Record<string, number>;
  outbox: OutboxEvent[];
  logs: Record<string, unknown>[];
}

export interface LocalDatabase {
  initialize(): Promise<void>;
  transaction<T>(work: () => Promise<T>): Promise<T>;
  apply(change: Change): Promise<void>;
  checkpoint(stream: string): Promise<number>;
  setCheckpoint(stream: string, sequence: number): Promise<void>;
  enqueue(event: OutboxEvent): Promise<void>;
  pendingOutbox(): Promise<OutboxEvent[]>;
  markOutbox(eventId: string, status: 'SENT' | 'FAILED'): Promise<void>;
  query(stream: string): Promise<Record<string, unknown>[]>;
  clear(): Promise<void>;
  log(level: string, message: string, details?: unknown): Promise<void>;
  diagnostics(): Promise<Pick<Diagnostics, 'checkpoints' | 'outbox' | 'logs'>>;
}

type Listener = () => void;

class OfflineSyncClient {
  private config?: SyncConfig;
  private status: SyncStatus = { state: 'UNINITIALIZED', pendingOutbox: 0 };
  private listeners = new Set<Listener>();
  private unsubscribeNetwork?: () => void;
  private running?: Promise<void>;

  async init(config: SyncConfig): Promise<void> {
    this.config = config;
    await config.database.initialize();
    this.unsubscribeNetwork?.();
    this.unsubscribeNetwork = config.network?.subscribe((online) => {
      if (online) void this.syncNow();
      else this.setStatus({ ...this.status, state: 'OFFLINE' });
    });
    this.setStatus({ state: 'IDLE', pendingOutbox: (await config.database.pendingOutbox()).length });
  }

  isInitialized(): boolean {
    return this.config !== undefined;
  }

  async registerDevice(): Promise<void> {
    const c = this.requireConfig();
    await this.request('/api/v1/devices/register', {
      method: 'POST',
      body: JSON.stringify({ deviceId: c.deviceId, pdvCode: c.pdvCode, appVersion: c.appVersion, platform: c.platform }),
    });
  }

  syncNow(): Promise<void> {
    if (this.running) return this.running;
    this.running = this.runSync().finally(() => {
      this.running = undefined;
    });
    return this.running;
  }

  private async runSync(): Promise<void> {
    const c = this.requireConfig();
    if (c.network && !(await c.network.isOnline())) {
      this.setStatus({ ...this.status, state: 'OFFLINE' });
      return;
    }
    this.setStatus({ ...this.status, state: 'SYNCING', error: undefined });
    try {
      await this.pushOutbox();
      for (const stream of c.streams) await this.pullStream(stream);
      this.setStatus({
        state: 'IDLE',
        lastSyncAt: new Date().toISOString(),
        pendingOutbox: (await c.database.pendingOutbox()).length,
      });
    } catch (error) {
      await c.database.log('ERROR', 'Sync failed', String(error));
      this.setStatus({
        ...this.status,
        state: 'ERROR',
        error: String(error),
        pendingOutbox: (await c.database.pendingOutbox()).length,
      });
      throw error;
    }
  }

  private async pullStream(stream: string): Promise<void> {
    const c = this.requireConfig();
    let checkpoint = await c.database.checkpoint(stream);
    if (checkpoint === 0 && c.bootstrapWithSnapshot) {
      const snapshot = await this.request<{ records: Change[]; checkpoint: number }>(
        '/api/v1/sync/snapshot?stream=' + encodeURIComponent(stream),
      );
      await c.database.transaction(async () => {
        for (const record of snapshot.records) await c.database.apply(record);
        await c.database.setCheckpoint(stream, snapshot.checkpoint);
      });
      checkpoint = snapshot.checkpoint;
    }
    let hasMore = true;
    while (hasMore) {
      const query = new URLSearchParams({ stream, checkpoint: String(checkpoint), limit: String(c.pageSize ?? 200) });
      if (c.pdvCode) query.set('routeKey', c.pdvCode);
      const page = await this.request<{ changes: Change[]; nextCheckpoint: number; hasMore: boolean }>(
        `/api/v1/sync/pull?${query}`,
      );
      await c.database.transaction(async () => {
        for (const change of page.changes) await c.database.apply(change);
        await c.database.setCheckpoint(stream, page.nextCheckpoint);
      });
      checkpoint = page.nextCheckpoint;
      hasMore = page.hasMore;
    }
    await this.request('/api/v1/sync/ack', {
      method: 'POST',
      body: JSON.stringify({ deviceId: c.deviceId, stream, checkpoint }),
    });
  }

  private async pushOutbox(): Promise<void> {
    const c = this.requireConfig();
    const events = await c.database.pendingOutbox();
    if (!events.length) return;
    const response = await this.request<{ results: { eventId: string; status: string }[] }>('/api/v1/sync/push', {
      method: 'POST',
      body: JSON.stringify({ deviceId: c.deviceId, events }),
    });
    for (const result of response.results) {
      await c.database.markOutbox(result.eventId, result.status === 'ACCEPTED' || result.status === 'DUPLICATE' ? 'SENT' : 'FAILED');
    }
  }

  async enqueueOutboxEvent(event: OutboxEvent): Promise<void> {
    const c = this.requireConfig();
    await c.database.enqueue(event);
    this.setStatus({ ...this.status, pendingOutbox: (await c.database.pendingOutbox()).length });
  }

  async clearLocalCache(): Promise<void> {
    await this.requireConfig().database.clear();
    this.setStatus({ state: 'IDLE', pendingOutbox: 0 });
  }

  getSyncStatus(): SyncStatus {
    return { ...this.status };
  }

  async getDiagnostics(): Promise<Diagnostics> {
    return { status: this.getSyncStatus(), ...(await this.requireConfig().database.diagnostics()) };
  }

  async query(stream: string): Promise<Record<string, unknown>[]> {
    if (!this.config) return [];
    return this.config.database.query(stream);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private async request<T = unknown>(path: string, init: { method?: string; headers?: Record<string, string>; body?: string } = {}): Promise<T> {
    const c = this.requireConfig();
    const doFetch = c.fetch ?? (globalThis.fetch as unknown as FetchLike);
    const response = await doFetch(`${c.baseUrl.replace(/\/$/, '')}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', 'X-API-Key': c.apiKey, ...init.headers },
    });
    if (!response.ok) throw new Error(`OfflineSync API ${response.status}: ${await response.text()}`);
    return (response.status === 204 ? undefined : await response.json()) as T;
  }

  private requireConfig(): SyncConfig {
    if (!this.config) throw new Error('OfflineSync.init(config) must be called first');
    return this.config;
  }

  private setStatus(status: SyncStatus): void {
    this.status = status;
    this.listeners.forEach((listener) => listener());
  }
}

export const OfflineSync = new OfflineSyncClient();

// ---- React hooks -----------------------------------------------------------

export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState(OfflineSync.getSyncStatus());
  useEffect(() => OfflineSync.subscribe(() => setStatus(OfflineSync.getSyncStatus())), []);
  return status;
}

/** Live rows of a stream from the local store; re-reads whenever sync state changes. */
export function useOfflineRows(stream: string): Record<string, unknown>[] {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      void OfflineSync.query(stream).then((next) => {
        if (active) setRows(next);
      });
    };
    refresh();
    const unsubscribe = OfflineSync.subscribe(refresh);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [stream]);
  return rows;
}

export function useOutbox(): Diagnostics['outbox'] {
  const [rows, setRows] = useState<Diagnostics['outbox']>([]);
  useEffect(() => {
    const refresh = () => void OfflineSync.getDiagnostics().then((d) => setRows(d.outbox));
    refresh();
    return OfflineSync.subscribe(refresh);
  }, []);
  return rows;
}
